import { useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import { gql } from '../lib/api';
import {
  DELETE_FIXED_COST_ITEM_MUTATION,
  FIXED_COST_ITEMS_QUERY,
  SET_FIXED_COST_ITEM_MUTATION,
  STORES_QUERY,
} from '../graphql/queries';

interface Store {
  id: string;
  name: string;
}

interface FixedCostItem {
  storeId: string;
  itemKey: string;
  itemName: string;
  amount: number;
  notes: string | null;
  updatedAt: string | null;
}

interface ItemDef {
  key: string;
  name: string;
}

// 家賃を最上段の既定項目として配置（itemKey="rent"）
const DEFAULT_FIXED_COST_ITEMS: ItemDef[] = [
  { key: 'rent', name: '家賃' },
  { key: 'communication', name: '通信費' },
  { key: 'utilities', name: '水道光熱費' },
  { key: 'lease', name: 'リース料' },
  { key: 'web_ad', name: 'Web 広告費' },
  { key: 'promotion', name: '販促費' },
  { key: 'supplies', name: '消耗品費' },
  { key: 'insurance', name: '保険料' },
  { key: 'consulting', name: '顧問料' },
  { key: 'others', name: 'その他' },
];

const slugify = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 32) || `item_${Date.now()}`;

export const FixedCostPage = () => {
  const qc = useQueryClient();

  const storesQ = useQuery({
    queryKey: ['stores'],
    queryFn: () => gql<{ stores: Store[] }>(STORES_QUERY),
  });
  const stores = storesQ.data?.stores ?? [];

  const itemQueries = useQueries({
    queries: stores.map((s) => ({
      queryKey: ['fixedCostItems', s.id],
      queryFn: () =>
        gql<{ fixedCostItems: FixedCostItem[] }>(FIXED_COST_ITEMS_QUERY, { storeId: s.id }),
      enabled: !!s.id,
    })),
  });

  // storeId × itemKey → { itemName, amount }
  const itemMap = useMemo(() => {
    const m: Record<string, Record<string, { itemName: string; amount: number }>> = {};
    stores.forEach((s, i) => {
      m[s.id] = {};
      const data = itemQueries[i]?.data?.fixedCostItems ?? [];
      for (const e of data) m[s.id][e.itemKey] = { itemName: e.itemName, amount: e.amount };
    });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stores, itemQueries.map((q) => q.dataUpdatedAt).join('|')]);

  // 表示する項目: 既定項目 + データに含まれる非既定項目 + ユーザー追加の extraItems
  const [extraItems, setExtraItems] = useState<ItemDef[]>([]);
  const items: ItemDef[] = useMemo(() => {
    const known = new Set(DEFAULT_FIXED_COST_ITEMS.map((it) => it.key));
    const extras: ItemDef[] = [];
    Object.values(itemMap).forEach((m) => {
      Object.entries(m).forEach(([k, v]) => {
        if (!known.has(k) && !extras.find((e) => e.key === k)) {
          extras.push({ key: k, name: v.itemName });
          known.add(k);
        }
      });
    });
    extraItems.forEach((it) => {
      if (!known.has(it.key)) {
        extras.push(it);
        known.add(it.key);
      }
    });
    return [...DEFAULT_FIXED_COST_ITEMS, ...extras];
  }, [itemMap, extraItems]);

  // ローカル編集 state
  const [draft, setDraft] = useState<Record<string, Record<string, string>>>({});

  const setItem = useMutation({
    mutationFn: (vars: { storeId: string; input: { itemKey: string; itemName: string; amount: number } }) =>
      gql(SET_FIXED_COST_ITEM_MUTATION, vars),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: ['fixedCostItems', vars.storeId] });
    },
  });

  const deleteItem = useMutation({
    mutationFn: (vars: { storeId: string; itemKey: string }) =>
      gql(DELETE_FIXED_COST_ITEM_MUTATION, vars),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: ['fixedCostItems', vars.storeId] });
    },
  });

  const getValue = (storeId: string, itemKey: string): string => {
    const d = draft[storeId]?.[itemKey];
    if (d !== undefined) return d;
    const v = itemMap[storeId]?.[itemKey]?.amount;
    return v != null ? String(v) : '';
  };

  const updateDraft = (storeId: string, itemKey: string, value: string) => {
    setDraft((prev) => ({
      ...prev,
      [storeId]: { ...prev[storeId], [itemKey]: value },
    }));
  };

  const saveCell = (storeId: string, itemKey: string, itemName: string) => {
    const value = draft[storeId]?.[itemKey];
    if (value === undefined || value === '') return;
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) {
      alert('金額は 0 以上の数値で入力してください。');
      return;
    }
    if (num === itemMap[storeId]?.[itemKey]?.amount) {
      // 値が変わっていなければ draft をクリアするだけ
      setDraft((prev) => {
        const next = { ...prev };
        if (next[storeId]) {
          const cell = { ...next[storeId] };
          delete cell[itemKey];
          next[storeId] = cell;
        }
        return next;
      });
      return;
    }
    setItem.mutate({
      storeId,
      input: { itemKey, itemName, amount: num },
    });
  };

  const addItem = () => {
    const name = prompt('追加する項目名を入力してください（例：駐車場代）');
    if (!name) return;
    const baseKey = slugify(name);
    let key = baseKey;
    let n = 1;
    const existing = new Set(items.map((it) => it.key));
    while (existing.has(key)) {
      n += 1;
      key = `${baseKey}_${n}`;
    }
    setExtraItems((prev) => [...prev, { key, name }]);
  };

  const removeItem = (item: ItemDef) => {
    if (item.key === 'rent') {
      alert('家賃は既定項目のため削除できません。金額を 0 にしたい場合は値を空にしてください。');
      return;
    }
    if (!confirm(`「${item.name}」を全店舗から削除します。よろしいですか？`)) return;
    for (const s of stores) {
      if (itemMap[s.id]?.[item.key] != null) {
        deleteItem.mutate({ storeId: s.id, itemKey: item.key });
      }
    }
    setExtraItems((prev) => prev.filter((it) => it.key !== item.key));
  };

  return (
    <div>
      <div style={pageHeader}>
        <h1 style={pageTitle}>固定費</h1>
        <button type="button" onClick={addItem} style={secondaryBtn}>
          ＋ 項目を追加
        </button>
      </div>

      <section style={notice}>
        <p style={{ margin: 0 }}>
          店舗別 × 項目別の<strong>月額固定値</strong>を入力します。家賃も同テーブルで管理（itemKey="rent"）。
          月次で大きく変動しない前提のため、変更時はここを更新するだけで以後の各月に反映されます。
          <br />
          <strong>※ 各セルから別の場所に移動すると自動保存</strong>されます。
        </p>
      </section>

      <section style={cardSection}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th
                  style={{
                    ...th,
                    minWidth: 180,
                    position: 'sticky',
                    left: 0,
                    background: '#f8f9fa',
                    zIndex: 1,
                  }}
                >
                  項目
                </th>
                {stores.map((s) => (
                  <th key={s.id} style={{ ...th, textAlign: 'right', minWidth: 140 }}>
                    {s.name}
                  </th>
                ))}
                <th style={{ ...th, textAlign: 'right', minWidth: 140, background: '#fff5f5' }}>
                  全社合計
                </th>
                <th style={{ ...th, width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const rowTotal = stores.reduce((sum, s) => {
                  const v = Number(getValue(s.id, it.key));
                  return sum + (Number.isFinite(v) ? v : 0);
                }, 0);
                return (
                  <tr key={it.key}>
                    <td
                      style={{
                        ...td,
                        minWidth: 180,
                        position: 'sticky',
                        left: 0,
                        background: it.key === 'rent' ? '#fff8e1' : '#fff',
                        zIndex: 1,
                        fontWeight: it.key === 'rent' ? 600 : 400,
                      }}
                    >
                      {it.name}
                      {it.key === 'rent' && (
                        <span style={{ marginLeft: 6, color: '#f08c00', fontSize: '0.7rem' }}>家賃</span>
                      )}
                    </td>
                    {stores.map((s, i) => (
                      <td key={s.id} style={{ ...td, padding: '0.2rem' }}>
                        <input
                          type="number"
                          step={1000}
                          min={0}
                          value={getValue(s.id, it.key)}
                          onChange={(e) => updateDraft(s.id, it.key, e.target.value)}
                          onBlur={() => saveCell(s.id, it.key, it.name)}
                          style={cellInput}
                          placeholder="—"
                          disabled={itemQueries[i]?.isLoading}
                        />
                      </td>
                    ))}
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700, background: '#fff5f5' }}>
                      ¥{rowTotal.toLocaleString()}
                    </td>
                    <td style={{ ...td, textAlign: 'center', padding: '0.2rem' }}>
                      <button
                        type="button"
                        onClick={() => removeItem(it)}
                        title={it.key === 'rent' ? '家賃は削除不可' : `${it.name} を削除`}
                        disabled={it.key === 'rent'}
                        style={{
                          ...removeBtn,
                          color: it.key === 'rent' ? '#ced4da' : '#aa0b56',
                          cursor: it.key === 'rent' ? 'not-allowed' : 'pointer',
                        }}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td
                  style={{
                    ...td,
                    fontWeight: 700,
                    background: '#f8f9fa',
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                  }}
                >
                  店舗別 月額合計
                </td>
                {stores.map((s) => {
                  const total = items.reduce((sum, it) => {
                    const v = Number(getValue(s.id, it.key));
                    return sum + (Number.isFinite(v) ? v : 0);
                  }, 0);
                  return (
                    <td
                      key={s.id}
                      style={{ ...td, textAlign: 'right', fontWeight: 700, background: '#f8f9fa' }}
                    >
                      ¥{total.toLocaleString()}
                    </td>
                  );
                })}
                <td
                  style={{
                    ...td,
                    textAlign: 'right',
                    fontWeight: 700,
                    background: '#aa0b56',
                    color: '#fff',
                  }}
                >
                  ¥
                  {stores
                    .reduce(
                      (s, st) =>
                        s +
                        items.reduce((m, it) => {
                          const v = Number(getValue(st.id, it.key));
                          return m + (Number.isFinite(v) ? v : 0);
                        }, 0),
                      0,
                    )
                    .toLocaleString()}
                </td>
                <td style={{ ...td, background: '#f8f9fa' }}></td>
              </tr>
            </tfoot>
          </table>
        </div>
        {(setItem.isError || deleteItem.isError) && (
          <p style={{ color: '#aa0b56', marginTop: '0.5rem' }}>
            保存エラー：
            {((setItem.error as Error | null) ?? (deleteItem.error as Error | null))?.message}
          </p>
        )}
        {(setItem.isPending || deleteItem.isPending) && (
          <p style={{ color: '#868e96', fontSize: '0.85rem', marginTop: '0.5rem' }}>保存中…</p>
        )}
      </section>
    </div>
  );
};

const pageHeader: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '1rem',
  flexWrap: 'wrap',
  gap: '0.5rem',
};
const pageTitle: React.CSSProperties = { margin: 0, fontSize: '1.4rem' };
const cardSection: React.CSSProperties = {
  background: '#fff',
  borderRadius: 2,
  padding: '1rem 1.25rem',
  boxShadow: 'none',
  border: '1px solid #d4d1cf',
  marginBottom: '1rem',
};
const notice: React.CSSProperties = {
  background: '#e7f5ff',
  border: '1px solid #a5d8ff',
  color: '#1864ab',
  borderRadius: 8,
  padding: '0.6rem 0.9rem',
  marginBottom: '1rem',
  fontSize: '0.85rem',
};
const cellInput: React.CSSProperties = {
  padding: '0.3rem 0.4rem',
  borderRadius: 4,
  border: '1px solid #d4d1cf',
  width: '100%',
  textAlign: 'right',
  fontSize: '0.85rem',
  background: '#fff',
};
const secondaryBtn: React.CSSProperties = {
  padding: '0.45rem 0.8rem',
  borderRadius: 6,
  border: '1px solid #ced4da',
  background: '#fff',
  color: '#495057',
  cursor: 'pointer',
  fontSize: '0.85rem',
};
const removeBtn: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  fontSize: '0.95rem',
  padding: '0 0.2rem',
  lineHeight: 1,
};
const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.85rem',
};
const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.5rem 0.6rem',
  borderBottom: '1px solid #dee2e6',
  background: '#f8f9fa',
  fontSize: '0.82rem',
};
const td: React.CSSProperties = {
  padding: '0.4rem 0.6rem',
  borderBottom: '1px solid #f1f3f5',
};
